import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { library } from '@cwos/api-client';
import { Button, Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { useNav } from '../navigation.js';
import { LoadFailure } from '../LoadFailure.js';
import { usePermissions } from '../permissions.js';
import { DraftWithPhibian, DraftedBy } from './PhibianDrafts.js';
import { ProposeReading, type ReadingDraft } from './ProposeReading.js';
import {
  bindingnessLabel,
  bindingnessVariant,
  effectiveWindow,
  extractionKindLabel,
  extractionStateLabel,
  formatDay,
  formatInstantDay,
  ingestionStateLabel,
  ruleStateLabel,
  ruleStateVariant,
} from './libraryLogic.js';

/**
 * ONE DOCUMENT, AND EVERYTHING THAT HAS BEEN MADE OF IT.
 *
 * The founder's requirement: the original source, its version, when it was
 * uploaded or retrieved, its authority, the knowledge extracted from it, the
 * passages that evidence supports, the approvals, the changes, and what relies
 * on it.
 *
 * ---------------------------------------------------------------------------
 * TWO THINGS THIS SCREEN SAYS OUT LOUD RATHER THAN IMPLYING
 * ---------------------------------------------------------------------------
 *
 * UPLOADING CHANGED NOTHING. A document sits here inert until a person approves
 * a reading of it, and that is enforced by a database trigger rather than by
 * this screen or the service behind it. So the page leads with what the
 * document HAS done — how many rules came out of it — rather than with its
 * text, because "we have your handbook" and "your handbook is in force" are the
 * two things a customer most often confuses.
 *
 * THE REJECTIONS ARE HERE TOO. A reading somebody turned down is a record of a
 * judgment, kept for the same reason the approvals are; a document history with
 * the disagreements edited out is not a history. `cwos_app` holds no DELETE on
 * that table precisely so this list cannot be curated.
 *
 * ---------------------------------------------------------------------------
 * WHY THE DOCUMENT'S TEXT IS NOT ON THIS PAGE
 * ---------------------------------------------------------------------------
 * `GET /knowledge/sources/:id` returns the document's METADATA and the readings
 * taken from it; the verbatim text comes back only from the search route, as
 * `Evidence`, wearing a type that says it is untrusted reference data. Putting
 * a handbook's full body on a detail page is a transmission-policy decision
 * about a store that is about to be wired to a model, not a layout decision,
 * and it is not this lane's to make. The page says where the text can be
 * reached instead.
 */
export function SourceDetail() {
  const navigate = useNav();
  const { me } = useAuth();
  const { has } = usePermissions();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false }) as { sourceId?: string };
  const sourceId = params.sourceId ?? '';
  const [previewQuery, setPreviewQuery] = useState('');
  const [appliedPreviewQuery, setAppliedPreviewQuery] = useState('');
  const [editingKind, setEditingKind] = useState(false);
  const [kindDraft, setKindDraft] = useState('');
  const [draft, setDraft] = useState<{ n: number; initial: ReadingDraft }>({ n: 0, initial: {} });
  const [justProposed, setJustProposed] = useState<string | null>(null);
  const mayWrite = has('knowledge:write');

  const detail = useQuery({
    queryKey: ['library', 'source', sourceId],
    queryFn: () => library.source(sourceId),
    enabled: sourceId !== '',
  });

  const classify = useMutation({
    mutationFn: (classification: string) => library.classifySource(sourceId, classification),
    onSuccess: () => {
      setEditingKind(false);
      void queryClient.invalidateQueries({ queryKey: ['library', 'source', sourceId] });
      void queryClient.invalidateQueries({ queryKey: ['library', 'sources'] });
    },
  });

  // The "readable preview" — this document's own title, searched, so a
  // reader can see an excerpt without this screen carrying the full text
  // itself (a decision the doctrine layer makes on purpose; see the header
  // note on why the text is not here). A person can also search their own
  // words below to find the passage they are looking for.
  const ownTitlePreview = useQuery({
    queryKey: ['library', 'search', 'own-title', sourceId],
    queryFn: () => library.searchSources(detail.data!.source.title, 5),
    enabled: detail.data !== undefined,
  });
  const customPreview = useQuery({
    queryKey: ['library', 'search', 'preview', sourceId, appliedPreviewQuery],
    queryFn: () => library.searchSources(appliedPreviewQuery, 5),
    enabled: appliedPreviewQuery !== '',
  });
  const previewResults = appliedPreviewQuery !== '' ? customPreview : ownTitlePreview;
  const ownExcerpt = (previewResults.data ?? []).find((r) => r.provenance.sourceId === sourceId);

  if (detail.isLoading) return <SkeletonSet surface="drawer" rows={5} />;
  if (detail.isError) {
    return (
      <LoadFailure
        error={detail.error}
        // A document outside the reader's programs answers 404, not 403 — a 403
        // would confirm it exists, and its existence is part of what is being
        // withheld. So the retryable sentence must not promise the document is
        // there.
        message="We couldn’t open this document just now. If it belongs to a program you don’t work in, it will not open for you at all."
        onRetry={() => void detail.refetch()}
        mainRead
        refusedHeading="This document is not shown to you"
      />
    );
  }

  const data = detail.data;
  if (!data) return null;
  const { source, extractions, rules } = data;
  const pending = extractions.filter(
    (e) => e.state === 'extraction_proposed' || e.state === 'awaiting_approval',
  );
  const decided = extractions.filter(
    (e) => e.state !== 'extraction_proposed' && e.state !== 'awaiting_approval',
  );

  return (
    <article className="rn-col">
      {/* THE RECORD. What this document IS — solid, in both themes. */}
      <header className="rn-card card-record">
        <h1 className="rn-card-title">{source.title}</h1>
        <p className="rn-row">
          <Pill variant="plain">{ingestionStateLabel(source.ingestionState)}</Pill>
          <Pill variant="sky">Stored — searchable</Pill>
        </p>
        <p className="rn-muted">
          {rules.length === 0
            ? 'Nothing in this document is in force. It is stored and searchable, and it changes nothing until somebody approves a reading of it.'
            : `${rules.length === 1 ? '1 rule' : `${rules.length} rules`} your organization enforces came out of this document.`}
        </p>
      </header>

      <section className="rn-card card-record">
        <h2 className="rn-card-title">About this document</h2>
        <dl>
          <dt>Source type</dt>
          <dd>Uploaded document</dd>

          <dt>Uploaded by</dt>
          <dd>
            {source.uploadedByUserId === null ? (
              <span className="rn-muted">Not recorded</span>
            ) : source.uploadedByUserId === me?.userId ? (
              'You'
            ) : (
              <span className="rn-muted">Another admin on your account</span>
            )}
          </dd>

          <dt>Version</dt>
          <dd>{source.versionLabel ?? <span className="rn-muted">Not versioned</span>}</dd>

          <dt>Added to the Library</dt>
          <dd>{formatInstantDay(source.retrievedAt)}</dd>

          <dt>Access scope</dt>
          <dd>
            {source.programId === null ? (
              'Organization-wide'
            ) : (
              <span className="rn-muted">Narrowed to one program</span>
            )}
          </dd>

          <dt>Effective</dt>
          <dd>
            {source.effectiveFrom ? (
              <>
                From {formatDay(source.effectiveFrom)}
                {source.effectiveTo ? ` to ${formatDay(source.effectiveTo)}` : ', no end date'}
              </>
            ) : (
              // NOT DATED is a real state. An undated document is not one that
              // started at the beginning of time.
              <span className="rn-muted">Nobody recorded when this takes effect</span>
            )}
          </dd>

          <dt>Kind</dt>
          <dd>
            {editingKind ? (
              <form
                className="kl-classify"
                onSubmit={(e) => {
                  e.preventDefault();
                  const value = kindDraft.trim();
                  if (value !== '') classify.mutate(value);
                }}
              >
                <input
                  value={kindDraft}
                  onChange={(ev) => setKindDraft(ev.target.value)}
                  placeholder="Handbook, Program manual, Policy notice…"
                  aria-label="Kind of document"
                  autoFocus
                />
                <Button type="submit" variant="primary" disabled={classify.isPending}>
                  {classify.isPending ? 'Saving…' : 'Save'}
                </Button>
                <button type="button" className="rn-link" onClick={() => setEditingKind(false)}>
                  Cancel
                </button>
              </form>
            ) : (
              <>
                {source.classification ?? (
                  // NULL means NOT YET CLASSIFIED, which is different from
                  // "classified as nothing". Both are honest; only one is true.
                  <span className="rn-muted">Not yet classified</span>
                )}
                {mayWrite ? (
                  <button
                    type="button"
                    className="rn-link"
                    onClick={() => {
                      setKindDraft(source.classification ?? '');
                      setEditingKind(true);
                    }}
                  >
                    {source.classification ? 'Change' : 'Add a kind'}
                  </button>
                ) : null}
              </>
            )}
            {classify.isError ? (
              <p className="rn-muted kl-error" role="alert">
                {classify.error instanceof Error
                  ? classify.error.message
                  : 'That kind could not be saved. Nothing changed.'}
              </p>
            ) : null}
          </dd>

          <dt>Authority</dt>
          <dd>
            Your organization’s own material. Rules approved from it are your policy, and an outside
            requirement still floors them.
          </dd>

          <dt>State or region</dt>
          <dd>{source.jurisdiction ?? <span className="rn-muted">Not narrowed to one</span>}</dd>

          <dt>Replaces</dt>
          <dd>
            {source.supersedesSourceId ? (
              <button
                type="button"
                onClick={() => navigate(`/library/sources/${source.supersedesSourceId!}`)}
              >
                An earlier document
              </button>
            ) : (
              <span className="rn-muted">Nothing — this is not a replacement</span>
            )}
          </dd>
        </dl>

        {source.instructionShaped ? (
          // A REVIEWER HINT, AND THE SCREEN SAYS SO. This document contains
          // text addressed to a machine, which is a strong sign it is not what
          // it claims to be. It is NOT a block: the document is stored,
          // searchable and just as inert as any other, because nothing
          // downstream was ever going to obey it. Rendering this as "blocked"
          // would teach an operator that the absence of the warning means the
          // text WAS trusted, which is exactly backwards.
          <p className="rn-muted" role="note">
            Worth a look: this document contains text written to be read by a computer rather than
            by a person ({source.instructionSignals.join(', ')}). Phibian does not act on it — no
            document can grant anyone permissions or change what Phibian is allowed to do — but text
            like this is a common sign a file is not what it claims to be.
          </p>
        ) : null}
      </section>

      {/*
        SOURCE / PREVIEW. The document's full text is deliberately not on
        this page — search is where it comes back, marked as reference data
        — so this IS the preview: a real search, scoped to this document by
        title unless a reader asks their own question, with the passage that
        matched shown as a quote. This is a solid record: it is the
        document's own words, not Phibian's interpretation of them.
      */}
      <section className="rn-card card-record">
        <h2 className="rn-card-title">Source</h2>
        <p className="rn-muted">
          The full text is not shown on this page. Search it — your own words, or leave the default
          below — and the matching passage comes back as a quote.
        </p>
        <form
          className="kl-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedPreviewQuery(previewQuery.trim());
          }}
        >
          <input
            value={previewQuery}
            onChange={(e) => setPreviewQuery(e.target.value)}
            placeholder="Search within this document…"
            aria-label="Search this document"
          />
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>
        {previewResults.isLoading ? (
          <SkeletonSet surface="table" rows={2} />
        ) : ownExcerpt ? (
          <blockquote className="kl-excerpt">{ownExcerpt.text}</blockquote>
        ) : appliedPreviewQuery !== '' ? (
          <p className="rn-muted">No passage in this document matches that search.</p>
        ) : (
          <p className="rn-muted">Nothing came back for this document’s own title.</p>
        )}
      </section>

      {/*
        PROPOSE A READING — the step that turns a stored document into a rule
        somebody can approve. Select a passage in the quote above, or write the
        rule out. Writers only; the review queue decides.
      */}
      {mayWrite && source.ingestionState === 'source_available' ? (
        <section className="rn-card intel-edge">
          <h2 className="rn-card-title">Propose a rule from this document</h2>
          <DraftWithPhibian sourceId={source.id} />
          <p className="rn-muted">
            Select a passage in the text above and press “Use the selected text”, or write the rule
            out. It goes to review; it is not in force until an approver accepts it.
          </p>
          <div className="rn-row kl-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const selected = window.getSelection()?.toString().trim() ?? '';
                if (selected !== '') {
                  setDraft((d) => ({ n: d.n + 1, initial: { statement: selected } }));
                }
              }}
            >
              Use the selected text
            </Button>
          </div>
          <ProposeReading
            key={draft.n}
            source={source.id}
            initial={draft.initial}
            onProposed={(extraction) => {
              setJustProposed(extraction.proposedStatement);
              void queryClient.invalidateQueries({ queryKey: ['library', 'source', sourceId] });
            }}
          />
          {justProposed !== null ? (
            <p role="status" className="rn-muted">
              Sent for review: “{justProposed}”. It is listed under “Waiting for a decision”.
            </p>
          ) : null}
        </section>
      ) : null}

      {/*
        WHAT PHIBIAN UNDERSTANDS. Interpretation, so glass — the topics are
        the SUBJECTS Phibian's readers have proposed extractions about, real
        data already on this response, presented as what Phibian has found
        worth asking about rather than as another rules table.
      */}
      {extractions.length > 0 ? (
        <section className="rn-card intel-edge">
          <h2 className="rn-card-title">What Phibian understands</h2>
          <p className="rn-muted">
            Topics somebody has proposed a reading about, from this document. A topic here is not
            yet a rule — see “Waiting for a decision” below for what has not been approved.
          </p>
          <ul className="rn-row kl-topics">
            {Array.from(new Set(extractions.map((e) => e.subject))).map((subject) => (
              <li key={subject} className="kl-topic-chip">
                {subject}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/*
        USED BY. Honest eligibility, not a claim. Only Ask Phibian can cite
        an APPROVED rule from this document, and only through doctrine.resolve
        — never the raw text, and never Insights, Recommendations, Agents or
        Automations, which do not read Library documents at all today. This
        section says so rather than implying a wider reach than the product
        actually has.
      */}
      <section className="rn-card intel-edge">
        <h2 className="rn-card-title">Used by</h2>
        <dl className="kl-usedby">
          <dt>Ask Phibian</dt>
          <dd>
            {rules.length > 0 ? (
              <span>
                Yes — {rules.length} approved {rules.length === 1 ? 'rule' : 'rules'} from this
                document may be cited, with this document named as the source.
              </span>
            ) : (
              <span className="rn-muted">
                Not yet. No reading from this document has been approved into a rule.
              </span>
            )}
          </dd>
          <dt>Insights, Recommendations, Agents, Automations</dt>
          <dd>
            <span className="rn-muted">
              Not yet, by design. These do not read Library documents directly — only an approved
              rule, through Ask Phibian, can be cited.
            </span>
          </dd>
        </dl>
      </section>

      <section className="rn-card card-record">
        <h2 className="rn-card-title">What this document put in force</h2>
        {rules.length === 0 ? (
          <SuccessEmpty
            heading="Nothing yet"
            message="No reading taken from this document has been approved, so it changes nothing about how Phibian answers."
          />
        ) : (
          <ul>
            {rules.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => navigate(`/library/rules/${r.id}`)}>
                  {r.statement}
                </button>{' '}
                <Pill variant={bindingnessVariant(r.bindingness)}>
                  {bindingnessLabel(r.bindingness)}
                </Pill>{' '}
                <Pill variant={ruleStateVariant(r.state)}>{ruleStateLabel(r.state)}</Pill>
                <p className="rn-muted">{effectiveWindow(r)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* A PROPOSAL, not yet a fact — glass, like the rest of the page's
          interpretation. */}
      <section className="rn-card intel-edge">
        <h2 className="rn-card-title">Waiting for a decision</h2>
        {pending.length === 0 ? (
          <p className="rn-muted">
            Nothing from this document is awaiting review. Everything proposed from it has been
            decided.
          </p>
        ) : (
          <ul>
            {pending.map((e) => (
              <li key={e.id}>
                <span className="rn-strong">{e.proposedStatement}</span>
                <p className="rn-muted">
                  {extractionKindLabel(e.kind)} · answers {e.subject} · proposed as{' '}
                  {bindingnessLabel(e.proposedBindingness)} · proposed{' '}
                  {formatInstantDay(e.proposedAt)}
                </p>
                <DraftedBy extraction={e} />
                <p className="rn-muted">
                  This is not in force. It becomes a rule only when a person approves it.
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* THE AUDIT — a decided fact, so solid. */}
      <section className="rn-card card-record">
        <h2 className="rn-card-title">Change history</h2>
        {decided.length === 0 ? (
          <p className="rn-muted">Nothing has been decided about this document yet.</p>
        ) : (
          <ol>
            {decided.map((e) => (
              <li key={e.id}>
                <span className="rn-strong">{extractionStateLabel(e.state)}</span>
                {e.decidedAt ? <> on {formatInstantDay(e.decidedAt)}</> : null}
                <p>{e.proposedStatement}</p>
                {/* A REFUSAL WITH ITS REASON. A rejection requires a note at
                    the API — a refusal nobody explained is not a decision — so
                    this is never a bare "rejected". */}
                {e.decisionNote ? <p className="rn-muted">Noted: “{e.decisionNote}”</p> : null}
              </li>
            ))}
          </ol>
        )}
      </section>
    </article>
  );
}
